import type {SelectionState} from './state'
import Product from '../../../types/product'
import ProductConfiguration from '../../../types/product-configuration'
import Term from '../../../types/term'
import type {ID} from '../../../types/id'
import type {Entity} from '../../../types/filter-entity/entity'
import type {Modality} from '../../../types/filter-entity/modality'
import type {Region} from '../../../types/filter-entity/region'
import type {CustomerGroup} from '../../../types/filter-entity/customer-group'
import type {ProductCollection} from '../../../types/filter-entity/product-collection'
import type {RootState} from '../../state'

export default {
  findRegionByCookieId: (state: SelectionState) => (cookieId: string) => {
    return state.availableFilterEntities.regions.find(region => region.cookieId === cookieId)
  },

  filteredProductConfigurations: (state: Object, getters: Object, rootState: Object): ProductConfiguration[] => {
    // For performance reasons, some of the filtering of configurations is done client side (as opposed to filtering by the products API):
    let filteredConfigurations = [...state.productConfigurations]
    const selectedCustomerGroups = getters.availableSelectedCustomerGroups
    if (selectedCustomerGroups.length > 0) {
      filteredConfigurations = filteredConfigurations.filter((configuration: ProductConfiguration) =>
        // Is the customerGroup linked to the configuration selected in the filter?
        selectedCustomerGroups.find(customerGroup => customerGroup.id === configuration.customerGroup.id))
    }

    const selectedProductCollections = getters.availableSelectedProductCollections
    if (selectedProductCollections.length > 0) {
      filteredConfigurations = filteredConfigurations.filter((configuration: ProductConfiguration) => {
        // Is the productCollection of the product linked to the configuration selected in the filter?
        const product = state.availableFilterEntities.products.find((product: Product) => product.id === configuration.product.id)
        return product &&
          selectedProductCollections.find(productCollection => productCollection.id === product.collection.id)
      })
    }

    const selectedModalities = getters.availableSelectedModalities
    if (selectedModalities.length > 0) {
      filteredConfigurations = filteredConfigurations.filter((configuration: ProductConfiguration) =>
        // Is the modality linked to the configuration selected in the filter?
        selectedModalities.find(modality => modality.id === configuration.modality.id)
      )
    }

    return filteredConfigurations
  },
  productCollectionsForConfigurations:
    (state: Object, getters: Object): ProductCollection[] => {
      // return only the product collections that are linked to one or more of the filteredProductConfigurations
      const filteredConfigurations = getters.filteredProductConfigurations
      return getters.sortedProductCollections.filter(
        (productCollection: ProductCollection) => !!filteredConfigurations.find((configuration: ProductConfiguration) => {
          const product: Product = state.availableFilterEntities.products.find((product: Product) => product.id === configuration.product.id)
          return product && product.collection.id === productCollection.id
        })
      )
    },
  productsByCollection:
    (state: Object, getters: Object) =>
      (collection: ProductCollection): Product[] => {
        // return only the products in the provided productCollection that are linked to one or more of the filteredProductConfigurations
        const filteredConfigurations = getters.filteredProductConfigurations
        return state.availableFilterEntities.products.filter((product: Product) =>
          product.collection.id === collection.id &&
          filteredConfigurations.find((configuration: ProductConfiguration) => configuration.product.id === product.id)
        )
      },
  customerGroupsByProduct:
    (state: Object, getters: Object) =>
      (product: Product): ProductConfiguration[] => {
        const customerGroupIds: ID[] = getters.configurationsByProduct(product).map(
          (configuration: ProductConfiguration) => configuration.customerGroup.id
        )

        return state.availableFilterEntities.customerGroups.filter((customerGroup: CustomerGroup) => customerGroupIds.includes(customerGroup.id))
      },

  configurationsByProduct:
    (state: Object) =>
      (product: ?Product): ProductConfiguration[] => {
        return state.productConfigurations.filter((configuration: ProductConfiguration) => product && configuration.product.id === product.id)
      },
  configurationsByProductAndCustomerGroup:
    (state: Object) =>
      (product: Product, customerGroup: CustomerGroup): ProductConfiguration[] => {
        return state.productConfigurations.filter((configuration: ProductConfiguration) => configuration.product.id === product.id &&
          configuration.customerGroup.id === customerGroup.id)
      },
  configurationsByProductSortedByTermDuration:
    (state: Object, getters: Object, rootState: Object) =>
      (product: Product): ProductConfiguration[] => {
        return getters['configurationsByProduct'](product).sort((
          a: ProductConfiguration, b: ProductConfiguration) => {
          const aTerm = rootState.selection.availableFilterEntities.terms.find((item: Term) => item.id === a.term.id)
          const bTerm = rootState.selection.availableFilterEntities.terms.find((item: Term) => item.id === b.term.id)
          return aTerm.termsPerYear - bTerm.termsPerYear
        }).reverse()
      },

  mostExpensiveConfigurationByProductAndCustomerGroup:
    (state: Object, getters: Object) =>
      (product: Product, customerGroup: CustomerGroup): ProductConfiguration[] =>
        getters.configurationsByProductAndCustomerGroup(product, customerGroup).sort((
          a: ProductConfiguration, b: ProductConfiguration) =>
          b.pricePerYear.amount - a.pricePerYear.amount)[0],

  /**
   * Returns the customer group whose configurations should be shown.
   * If a customerGroup is selected in the selection, that one is used.
   * If no group is selected, the default customer group is used, if configurations for that group are available.
   * If no group is selected, and no configurations for the default customerGroup are available, the customerGroup with
   * the lowest order is used
   *
   * N.B. showing one customer group per product assumes all configurations for a product have the same
   * customer groups. This may not always be the case!
   * @returns string|null
   */
  customerGroupToShowForProduct:
    (state: SelectionState, getters: Object) =>
      (product: ?Product) => {
        // first collect all the data we may need. Even if we don't need them all in every execution,
        // vue must know we have a dependency upon them all.
        const filteredCustomerGroups = getters.availableSelectedCustomerGroups
        const filteredCustomerGroup = filteredCustomerGroups[0]
        const customerGroups = getters['customerGroupsByProduct'](product)
        const configurations = getters['configurationsByProductSortedByTermDuration'](product)

        const lowestPricePerYear = Math.min(...configurations.map(
          configuration => configuration.pricePerYear.amount))
        const leastExpensiveCustomerGroups = configurations
          .filter(configuration => configuration.pricePerYear.amount === lowestPricePerYear)
          .map(configuration => state.availableFilterEntities.customerGroups.find((customerGroup) => customerGroup.id === configuration.customerGroup.id))

        if (filteredCustomerGroup) {
          return filteredCustomerGroup
        }
        const defaultCustomerGroup = customerGroups.find(customerGroup => customerGroup.isDefault)
        if (defaultCustomerGroup) {
          return defaultCustomerGroup
        }
        if (leastExpensiveCustomerGroups.length > 0) {
          return leastExpensiveCustomerGroups.sort((a, b) => a.order - b.order)[0]
        }

        return null
      },

  availableFilterEntities (state: SelectionState, getters: any, rootState: Object, rootGetters: any) {
    return Object.entries(state.availableFilterEntities).reduce(
      (entities, entry: [string, Array<Entity>]) => {
        return {
          ...entities,
          [entry[0]]: entry[1].filter(entity => rootGetters['shop/shopFilterEntityIds'][entry[0]].includes(entity.id))
        }
      },
      {}
    )
  },
  sortedCustomerGroups (state: SelectionState, getters: any) {
    return [...getters.availableFilterEntities.customerGroups].sort((a, b) => a.order - b.order)
  },
  sortedModalities (state: SelectionState, getters: any) {
    return [...getters.availableFilterEntities.modalities].sort((a, b) => a.title.localeCompare(b.title))
  },
  sortedProductCollections (state: SelectionState, getters: any) {
    return [...getters.availableFilterEntities.productCollections].sort((a, b) => a.order - b.order)
  },
  sortedRegions (state: SelectionState, getters: any) {
    return [...getters.availableFilterEntities.regions].sort((a, b) => a.title.localeCompare(b.title))
  },

  sortedSelectedCustomerGroups (state: SelectionState): CustomerGroup[] {
    return [...state.selectedFilterEntities.customerGroups].sort((a, b) => a.order - b.order)
  },
  sortedSelectedProductCollections (state: SelectionState): ProductCollection[] {
    return [...state.selectedFilterEntities.productCollections].sort((a, b) => a.order - b.order)
  },
  sortedSelectedModalities (state: SelectionState): Modality[] {
    return [...state.selectedFilterEntities.modalities].sort((a, b) => a.title.localeCompare(b.title))
  },
  sortedSelectedRegions (state: SelectionState): Region[] {
    return [...state.selectedFilterEntities.regions].sort((a, b) => a.title.localeCompare(b.title))
  },

  availableSelectedCustomerGroups (state: SelectionState, getters: Object, rootState: RootState, rootGetters: Object): CustomerGroup[] {
    return getters.sortedSelectedCustomerGroups
      .filter(customerGroup => rootGetters['shop/shopFilterEntityIds'].customerGroups.includes(customerGroup.id))
  },
  availableSelectedProductCollections (state: SelectionState, getters: Object, rootState: RootState, rootGetters: Object): ProductCollection[] {
    return getters.sortedSelectedProductCollections
      .filter(productCollection => rootGetters['shop/shopFilterEntityIds'].productCollections.includes(productCollection.id))
  },
  availableSelectedModalities (state: SelectionState, getters: Object, rootState: RootState, rootGetters: Object): Modality[] {
    return getters.sortedSelectedModalities
      .filter(modality => rootGetters['shop/shopFilterEntityIds'].modalities.includes(modality.id))
  },
  availableSelectedRegions (state: SelectionState, getters: Object, rootState: RootState, rootGetters: Object): Region[] {
    return getters.sortedSelectedRegions.filter(region => rootGetters['shop/shopFilterEntityIds'].regions.includes(region.id))
  },

  sortedAvailableFilterEntities (
    state: SelectionState,
    getters: {
      sortedSelectedCustomerGroups: CustomerGroup[],
      sortedSelectedProductCollections: ProductCollection[],
      sortedSelectedModalities: Modality[],
      sortedSelectedRegions: Region[]
    }
  ): Array<Entity> {
    return [
      ...getters.sortedSelectedRegions,
      ...getters.sortedSelectedCustomerGroups,
      ...getters.sortedSelectedProductCollections,
      ...getters.sortedSelectedModalities
    ]
  },

  sortedSelectedFilterEntities (
    state: SelectionState,
    getters: {
      availableSelectedCustomerGroups: CustomerGroup[],
      availableSelectedProductCollections: ProductCollection[],
      availableSelectedModalities: Modality[],
      availableSelectedRegions: Region[]
    }
  ): Array<Entity> {
    return [
      ...getters.availableSelectedRegions,
      ...getters.availableSelectedCustomerGroups,
      ...getters.availableSelectedProductCollections,
      ...getters.availableSelectedModalities
    ]
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/selection/getters.js